import { Set } from 'immutable';

import { getTaxonomyMenuItem } from '@/utils/bulkEdit/taxonomy';
import { getSize } from '@/utils/iterable/getSize';
import { reduce } from '@/utils/iterable/reduce';

import { ATTRIBUTES_TO_MENU_ITEM_MAP, ATTRIBUTE_TO_MENU_ITEM_MAP, MENU_ITEM } from '@/constants/bulkEdit';
import { ERROR, TAXONOMY_PROPERTY, VALUE } from '@/constants/product';
import { EBAY, ETSY } from '@/constants/channels';
import { ATTRIBUTE } from '@/constants/attributes';
import { EMPTY } from '@/constants';
import { FORM } from '@/constants/forms';

export function shapeErrorCountsForApp({ channel, data }) {
  function reduceTaxonomyItems(result, value, key) {
    return result.set(getTaxonomyMenuItem(key), parseInt(value));
  }

  function reduceErrorCounts(result, value, key) {
    if (channel === EBAY && key === 'item_specifics_error_count') {
      return reduce(value, result, reduceTaxonomyItems);
    }

    const menuItem = ATTRIBUTES_TO_MENU_ITEM_MAP[channel][key.replace('_error_count', '')];

    return menuItem
      ? result.set(menuItem, parseInt(value))
      : result;
  }

  return reduce(data, EMPTY.MAP, reduceErrorCounts);
}

function getSectionsByErrorKey({ channel, key, product, value }) {
  function filterCustomItemSpecifics(item) {
    return product.getIn([FORM.DETAILS, ATTRIBUTE.ITEM_SPECIFICS, VALUE, item, 'type']) !== TAXONOMY_PROPERTY.CUSTOM;
  }

  switch (key) {
    case ATTRIBUTE.DESCRIPTION: {
      return channel === ETSY
        ? MENU_ITEM.DESCRIPTION
        : MENU_ITEM.BODY_HTML;
    }

    case ATTRIBUTE.WEIGHT: {
      return channel === ETSY
        ? MENU_ITEM.ITEM_WEIGHT
        : MENU_ITEM.WEIGHT;
    }

    case ATTRIBUTE.ITEM_SPECIFICS: {
      return value.keySeq().filter(filterCustomItemSpecifics).toSet().map(getTaxonomyMenuItem);
    }

    case ATTRIBUTE.PRODUCT_DETAILS:
    case ATTRIBUTE.TAXONOMY_ATTRIBUTES: {
      return value.keySeq().toSet().map(getTaxonomyMenuItem);
    }

    default: {
      return /^variations/.test(key)
        ? MENU_ITEM.VARIATIONS
        : ATTRIBUTE_TO_MENU_ITEM_MAP[key];
    }
  }
}

function decrementCount(count = 0) {
  return Math.max(count - 1, 0);
}

function incrementCount(count = 0) {
  return count + 1;
}

export function setErrorCounts({ channel, counts, products }) {
  function reduceProducts(result, product) {
    if (!product) {
      return result;
    }

    function reduceErrors(errors, value, key) {
      const sections = getSectionsByErrorKey({ channel, key, product, value });
      return sections
        ? Set.isSet(sections)
          ? errors.union(sections)
          : errors.add(sections)
        : errors;
    }

    function reduceErrorKeys(errors, key) {
      return errors.update(key, incrementCount);
    }

    return product.get(ERROR)
      .reduce(reduceErrors, EMPTY.SET)
      .reduce(reduceErrorKeys, result);
  }

  return products.get('byId').reduce(reduceProducts, counts);
}

export function updateErrorCounts({ channel, oldProduct, newProduct }) {
  function getSectionsWithErrors(product) {
    function reduceErrors(errors, value, key) {
      const sections = getSectionsByErrorKey({ channel, key, product, value });
      return sections
        ? Set.isSet(sections)
          ? errors.union(sections)
          : errors.add(sections)
        : errors;
    }

    return product.get(ERROR, EMPTY.MAP).reduce(reduceErrors, EMPTY.SET);
  }

  function reduceSections({ current, old }) {
    return function reduceKeys(result, key) {
      if (old.has(key) && !current.has(key)) {
        result.toDecrement = result.toDecrement.push(key);
      } else if (!old.has(key) && current.has(key)) {
        result.toIncrement = result.toIncrement.push(key);
      }

      return result;
    };
  }

  function incrementKeyCount(result, key) {
    return result.update(key, incrementCount);
  }

  function decrementKeyCount(result, key) {
    return result.update(key, decrementCount);
  }

  return function updateCounts(source = EMPTY.MAP) {
    let counts = source;
    const oldSections = getSectionsWithErrors(oldProduct);
    const currentSections = getSectionsWithErrors(newProduct);
    const { toDecrement, toIncrement } = oldSections.union(currentSections).reduce(
      reduceSections({ current: currentSections, old: oldSections }),
      { toDecrement: EMPTY.LIST, toIncrement: EMPTY.LIST }
    );

    if (getSize(toDecrement)) {
      counts = toDecrement.reduce(decrementKeyCount, counts);
    }

    if (getSize(toIncrement)) {
      counts = toIncrement.reduce(incrementKeyCount, counts);
    }

    return counts;
  };
}
